import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  correctionRequestService,
  type ApplyResponse,
  type CorrectionClinicalDateFilters,
  type CorrectionWorkQueueFilters,
} from "../services/correctionRequestService";
import type { DateOnlyString } from "../utils/dateOnly";

export function useAllCorrectionRequests(
  filters: CorrectionClinicalDateFilters & {
    page: number;
    limit?: number;
    dateFrom?: DateOnlyString;
    dateTo?: DateOnlyString;
    search?: string;
  }
) {
  const limit = filters.limit ?? 20;
  const offset = (filters.page - 1) * limit;
  return useQuery({
    queryKey: [
      "all-correction-requests",
      filters.page,
      limit,
      filters.dateFrom,
      filters.dateTo,
      filters.dateReceivedFrom,
      filters.dateReceivedTo,
      filters.dateCollectedFrom,
      filters.dateCollectedTo,
      filters.dateReportedFrom,
      filters.dateReportedTo,
      filters.search,
    ],
    queryFn: () =>
      correctionRequestService.listAllCorrectionRequests({
        limit,
        offset,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        dateReceivedFrom: filters.dateReceivedFrom,
        dateReceivedTo: filters.dateReceivedTo,
        dateCollectedFrom: filters.dateCollectedFrom,
        dateCollectedTo: filters.dateCollectedTo,
        dateReportedFrom: filters.dateReportedFrom,
        dateReportedTo: filters.dateReportedTo,
        search: filters.search,
      }),
  });
}

export function useUpdateCorrectionRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: string;
      caseId: string;
      body: Record<string, any>;
    }) => correctionRequestService.updateCorrectionRequest(requestId, body),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["correction-requests", variables.caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["all-correction-requests"],
        }),
      ]);
      notifications.show({
        title: "Updated",
        message: "Correction request updated.",
        color: "blue",
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      notifications.show({
        title: "Error",
        message:
          error.response?.data?.message ||
          "Failed to update correction request",
        color: "red",
      });
    },
  });
}

export function useCorrectionRequests(
  caseId: string | undefined,
  statusFilter?: string,
  options?: { includeHistory?: boolean }
) {
  const includeHistory = options?.includeHistory ?? false;

  return useQuery({
    queryKey: ["correction-requests", caseId, statusFilter, includeHistory],
    queryFn: () =>
      correctionRequestService.getCorrectionRequests(
        caseId!,
        statusFilter,
        50,
        0,
        includeHistory
      ),
    enabled: !!caseId,
  });
}

export function useActOnCorrectionRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      action,
      appliedChanges,
      note,
    }: {
      requestId: string;
      action: "apply" | "modify_and_apply" | "discard";
      appliedChanges?: Record<string, unknown>;
      note?: string;
      caseId: string;
    }) => {
      if (action === "apply") {
        return correctionRequestService.applyCorrectionRequest(requestId, note);
      }
      if (action === "modify_and_apply") {
        return correctionRequestService.applyModifiedCorrectionRequest(
          requestId,
          appliedChanges || {},
          note
        );
      }
      return correctionRequestService.discardCorrectionRequest(requestId, note);
    },
    onSuccess: async (_data: ApplyResponse, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["correction-requests", variables.caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["case", variables.caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["case-for-pathologist-review", variables.caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["document-view", variables.caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["report-preview", variables.caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["pathologist-work-queue"],
        }),
      ]);

      const messages: Record<string, string> = {
        apply: "Correction applied successfully.",
        modify_and_apply: "Correction modified and applied.",
        discard: "Correction request discarded.",
      };
      notifications.show({
        title: "Success",
        message: messages[variables.action] || "Action completed.",
        color: variables.action === "discard" ? "orange" : "green",
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      notifications.show({
        title: "Error",
        message:
          error.response?.data?.message ||
          "Failed to process correction request",
        color: "red",
      });
    },
  });
}

export function useCorrectionRequestsWorkQueue(limit = 50, offset = 0) {
  return useQuery({
    queryKey: ["pathologist-work-queue", limit, offset],
    queryFn: () => correctionRequestService.getWorkQueue(limit, offset),
  });
}

export function useInfiniteCorrectionRequestsWorkQueue(
  filters: CorrectionWorkQueueFilters = {},
  options: { enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    queryKey: ["pathologist-work-queue", "paged", filters],
    queryFn: ({ pageParam }) =>
      correctionRequestService.getPagedWorkQueue(filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled: options.enabled,
  });
}

export function usePendingCorrectionsForCase(
  caseId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["pending-corrections-for-case", caseId],
    queryFn: () =>
      correctionRequestService.getCorrectionRequests(caseId!, "pending", 100),
    enabled: enabled && !!caseId,
    staleTime: 30_000,
  });
}

export function useLinkableCorrectionsForCase(
  caseId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["linkable-corrections-for-case", caseId],
    queryFn: () =>
      correctionRequestService.getCorrectionRequests(caseId!, undefined, 100),
    enabled: enabled && !!caseId,
    staleTime: 30_000,
  });
}

export function useLinkCorrectionToErrorReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      caseErrorReportId,
    }: {
      requestId: string;
      caseErrorReportId: string | null;
    }) =>
      correctionRequestService.linkToErrorReport(requestId, caseErrorReportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-correction-requests"] });
      queryClient.invalidateQueries({ queryKey: ["error-reports"] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to link error report",
        color: "red",
      });
    },
  });
}
